import React, { useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import PhoneNumberHeader from "./PhoneNumberHeader";
import AssignUserList from "./AssignUserList";
import CustomTypography from "../../../commonComponents/CustomTypography";
import DebounceInput from "../../../commonComponents/DebounceInput";
import useFetchUsers from "../../../../hooks/useFetchUsers";
import CustomSkeleton from "../../../commonComponents/CustomSkeleton";
import { getItemIdentifier } from "./gotoHelpers";
import { getCompanyId } from "../../../../redux/slices/companySlice";
import useLang from "../../../../hooks/useLang";

const AssignUserDrawer = ({
  phoneData,
  extensionData,
  userData,
  deviceData,
  locationId,
  onUserSelect,
}) => {
  const [gotoLang] = useLang("GoTo");
  const [searchText, setSearchText] = useState("");
  const companyId = useSelector(getCompanyId);

  const isAdmin = window.location.pathname?.includes("admin-dashboard");

  // Fetch all users based on locationId and search
  const { users, loading } = useFetchUsers(null, null, !companyId || !locationId, {
    useAllUsersPayload: isAdmin,
    companyId: companyId ? parseInt(companyId, 10) : null,
    locationIds: locationId ? [parseInt(locationId, 10)] : [],
    search: searchText,
    isLocationFilter: false,
  });

  // Get current user ID and data from all 4 types: phone, extension, user, device
  const currentUser = phoneData?.user || extensionData?.user || userData?.user || deviceData?.user;
  const currentUserId = currentUser?.id;

  // Get the item identifier (number, name, etc.) for the header using helper function
  const itemIdentifier = getItemIdentifier(phoneData, extensionData, userData, deviceData);

  return (
    <div>
      {/* Header showing current assignment */}
      <div className="mb-6">
        <PhoneNumberHeader
          phoneNumber={itemIdentifier}
          assignedTo={currentUser?.fullName || null}
          assignedToRole={currentUser?.role?.name || null}
          label={gotoLang.currentlyAssignedTo}
        />
      </div>

      <div className="my-6">
        <CustomTypography variant="text" strong>
          {gotoLang.selectUserToAssign}
        </CustomTypography>

        <DebounceInput
          className="mt-5"
          placeholder={gotoLang.searchUserByName}
          value={searchText}
          onChange={setSearchText}
          suffix={<SearchOutlined className="icon-color" />}
          allowClear
        />
      </div>

      {/* Users List - Scrollable with fixed height */}
      <div className="overflow-y-auto pr-2" style={{ height: "calc(100vh - 376px)" }}>
        {loading ? (
          <CustomSkeleton
            skeletonCount={5}
            skeletonWidth="34rem"
            skeletonHeight="3rem"
            skeletonMarginBottom="15px"
          />
        ) : (
          <AssignUserList
            users={users}
            onUserSelect={onUserSelect}
            initialSelectedUserId={currentUserId}
          />
        )}
      </div>
    </div>
  );
};

export default AssignUserDrawer;
